import React, { useState, useEffect } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { apiService } from '../../services/api.service';

interface SummaryItem {
  combo: string;
  count: number;
  total_paid_crores: number;
}

interface BeneficiaryRecord {
  beneficiaryId: string;
  name: string;
  combo: string;
  missing_stages: string;
  has_bl: number;
  has_rl: number;
  has_rc: number;
  payment_methods: string;
  bl_amount: number;
  rl_amount: number;
  rc_amount: number;
  total_paid: number;
  bank_name: string;
}

interface SkippedStagesData {
  summary: SummaryItem[];
  records: BeneficiaryRecord[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

const comboLabels: Record<string, string> = {
  'BL+RC_NO_RL': 'BL + RC (RL Missing)',
  'RL+RC_NO_BL': 'RL + RC (BL Missing)',
  'RL_ONLY_NO_BL': 'RL Only (BL Missing)',
  'RC_ONLY': 'RC Only (BL, RL Missing)',
};

const comboColors: Record<string, string> = {
  'BL+RC_NO_RL': 'bg-orange-100 text-orange-800 border-orange-300',
  'RL+RC_NO_BL': 'bg-red-100 text-red-800 border-red-300',
  'RL_ONLY_NO_BL': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'RC_ONLY': 'bg-purple-100 text-purple-800 border-purple-300',
};

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-IN').format(num);
};

const formatRupees = (rupees: number): string => {
  if (rupees >= 10000000) return `${(rupees / 10000000).toFixed(2)}Cr`;
  if (rupees >= 100000) return `${(rupees / 100000).toFixed(2)}L`;
  if (rupees >= 1000) return `${(rupees / 1000).toFixed(1)}K`;
  return `${rupees}`;
};

export default function SkippedStages() {
  const [data, setData] = useState<SkippedStagesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCombo, setSelectedCombo] = useState<string>('');
  const [page, setPage] = useState(1);
  const [searchId, setSearchId] = useState('');
  const limit = 50;

  const fetchData = async (combo: string, pageNum: number) => {
    setLoading(true);
    try {
      const result = await apiService.getSkippedStages(combo || undefined, pageNum, limit);
      setData(result);
    } catch (error) {
      console.error('Failed to fetch skipped stages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedCombo, page);
  }, [selectedCombo, page]);

  const handleComboFilter = (combo: string) => {
    setSelectedCombo(combo === selectedCombo ? '' : combo);
    setPage(1);
  };

  const filteredRecords = data?.records?.filter(r =>
    !searchId || r.beneficiaryId.toLowerCase().includes(searchId.toLowerCase()) ||
    (r.name && r.name.toLowerCase().includes(searchId.toLowerCase()))
  ) || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-3 bg-orange-500 rounded-lg">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Skipped Stage Beneficiaries</h1>
            <p className="text-sm text-gray-600">
              Beneficiaries where Kotak Bank paid some stages directly — response files not uploaded
            </p>
          </div>
        </div>

        {/* Summary Cards */}
        {data?.summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {data.summary.map((item) => (
              <div
                key={item.combo}
                onClick={() => handleComboFilter(item.combo)}
                className={`rounded-xl p-5 border-2 cursor-pointer transition-all ${
                  selectedCombo === item.combo
                    ? 'ring-2 ring-blue-500 border-blue-500 shadow-md'
                    : `${comboColors[item.combo] || 'bg-gray-100 text-gray-800 border-gray-300'} hover:shadow-md`
                }`}
              >
                <p className="text-sm font-medium mb-1">{comboLabels[item.combo] || item.combo}</p>
                <p className="text-3xl font-bold">{formatNumber(item.count)}</p>
                <p className="text-xs mt-2 opacity-75">
                  Total Paid: {formatRupees(item.total_paid_crores * 10000000)}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Search + Info */}
        <div className="flex items-center justify-between">
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Beneficiary ID or Name..."
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>
          <p className="text-sm text-gray-600">
            Total: <span className="font-bold">{formatNumber(data?.total || 0)}</span> beneficiaries
          </p>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">#</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Beneficiary ID</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Type</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-700">Missing Stage</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-700">BL</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-700">RL</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-700">RC</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-700">BL Amt</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-700">RL Amt</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-700">RC Amt</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-700">Total Paid</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Payment Via</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((rec, idx) => (
                    <tr key={rec.beneficiaryId} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-500">{(data!.page - 1) * limit + idx + 1}</td>
                      <td className="px-4 py-3 font-mono text-xs">{rec.beneficiaryId}</td>
                      <td className="px-4 py-3">{rec.name || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${comboColors[rec.combo] || 'bg-gray-100'}`}>
                          {comboLabels[rec.combo] || rec.combo}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-block px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs font-bold">
                          {rec.missing_stages}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {rec.has_bl ? <span className="text-green-600 font-bold">Y</span> : <span className="text-red-400">N</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {rec.has_rl ? <span className="text-green-600 font-bold">Y</span> : <span className="text-red-400">N</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {rec.has_rc ? <span className="text-green-600 font-bold">Y</span> : <span className="text-red-400">N</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs">
                        {rec.bl_amount > 0 ? `${formatNumber(rec.bl_amount)}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs">
                        {rec.rl_amount > 0 ? `${formatNumber(rec.rl_amount)}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs">
                        {rec.rc_amount > 0 ? `${formatNumber(rec.rc_amount)}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-bold">
                        {formatNumber(rec.total_paid)}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {(rec.payment_methods || '').replace('BANK', 'Kotak')}
                      </td>
                    </tr>
                  ))}
                  {filteredRecords.length === 0 && (
                    <tr>
                      <td colSpan={13} className="px-4 py-8 text-center text-gray-500">No records found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {data && data.total_pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Page {data.page} of {data.total_pages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>
              <button
                onClick={() => setPage(Math.min(data.total_pages, page + 1))}
                disabled={page >= data.total_pages}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
